import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { usePageSeo } from '../../utils/seo';

export default function Partners() {
  usePageSeo({
    title: 'Партнёрам | Promokod.online',
    description: 'Условия сотрудничества с Promokod.online для брендов, магазинов и партнёров. Размещение промокодов, акции, трафик и совместные кампании.',
    canonicalPath: '/partners',
  });

  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    website: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const benefits = [
    {
      icon: 'ri-group-line',
      title: 'Широкая аудитория',
      description: 'Доступ к тысячам активных пользователей, которые ищут выгодные предложения каждый день'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Рост продаж',
      description: 'Увеличение конверсии и среднего чека благодаря целевому трафику заинтересованных покупателей'
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Детальная аналитика',
      description: 'Полная статистика по использованию промокодов и эффективности рекламных кампаний'
    },
    {
      icon: 'ri-hand-coin-line',
      title: 'Гибкие условия',
      description: 'Индивидуальный подход к каждому партнёру и выгодные условия сотрудничества'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Заявка',
      description: 'Заполните форму обратной связи или напишите нам на email'
    },
    {
      number: '02',
      title: 'Обсуждение',
      description: 'Наш менеджер свяжется с вами для обсуждения условий сотрудничества'
    },
    {
      number: '03',
      title: 'Договор',
      description: 'Подписываем договор и настраиваем интеграцию вашего магазина'
    },
    {
      number: '04',
      title: 'Запуск',
      description: 'Размещаем ваши промокоды и начинаем привлекать клиентов'
    }
  ];

  const formats = [
    {
      icon: 'ri-coupon-3-line',
      title: 'Размещение промокодов',
      description: 'Публикация ваших актуальных промокодов и специальных предложений на нашей платформе'
    },
    {
      icon: 'ri-megaphone-line',
      title: 'Рекламные кампании',
      description: 'Продвижение вашего магазина через баннеры, спецразмещение и email-рассылки'
    },
    {
      icon: 'ri-star-line',
      title: 'Эксклюзивные акции',
      description: 'Создание уникальных промокодов только для пользователей нашего сервиса'
    },
    {
      icon: 'ri-article-line',
      title: 'Контент-проекты',
      description: 'Обзоры, статьи и специальные материалы о вашем магазине и продуктах'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.company || !formData.name || !formData.email) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6cl8p2ff40lgbk6d060', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          website: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Станьте нашим партнёром
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Присоединяйтесь к ведущей платформе промокодов и увеличьте продажи вашего интернет-магазина. 
              Мы поможем привлечь новых клиентов и повысить лояльность существующих.
            </p>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Преимущества сотрудничества
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${benefit.icon} text-3xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How to Become Partner Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Как стать партнёром
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 h-full">
                    <div className="text-5xl font-bold text-blue-100 mb-4">
                      {step.number}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cooperation Formats Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Форматы сотрудничества
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {formats.map((format, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-8 border border-gray-200">
                  <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${format.icon} text-2xl text-orange-500`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {format.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {format.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Свяжитесь с нами
              </h2>
              <p className="text-gray-600">
                Заполните форму, и наш менеджер свяжется с вами в ближайшее время
              </p>
            </div>

            <form 
              id="partners-form" 
              data-readdy-form 
              onSubmit={handleSubmit}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Название компании <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="ООО «Ваша компания»"
                  />
                </div>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Контактное лицо <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Иван Иванов"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Телефон
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="+7 (999) 123-45-67"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                  Сайт магазина
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="https://example.com"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Сообщение
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder="Расскажите о вашем магазине и интересующих форматах сотрудничества..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length}/500
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center text-green-800">
                    <i className="ri-checkbox-circle-line text-xl mr-2"></i>
                    <span className="text-sm font-medium">Спасибо! Ваша заявка отправлена. Мы свяжемся с вами в ближайшее время.</span>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center text-red-800">
                    <i className="ri-error-warning-line text-xl mr-2"></i>
                    <span className="text-sm font-medium">Ошибка отправки. Пожалуйста, проверьте заполнение полей и попробуйте снова.</span>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
              </button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-gray-600 text-sm mb-2">
                Или напишите нам напрямую:
              </p>
              <a 
                href="mailto:partners@promokod.online" 
                className="text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
              >
                partners@promokod.online
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
